import { get } from "lodash"
import { rj, useRunRj } from "react-rocketjump"
import rjDebounce from "react-rocketjump/plugins/debounce"
import rjList, {
  nextPreviousPaginationAdapter
} from "react-rocketjump/plugins/list"
import api from "../api"

const getOrSelect = (context, pathOrFunction) => {
  if (typeof pathOrFunction === "function") {
    return pathOrFunction(context)
  }
  return get(context, pathOrFunction)
}

const CustomersListRj = rj(
  rjDebounce(),
  rjList({
    pageSize: 100,
    pagination: nextPreviousPaginationAdapter,
    customPaginationReducer: (prevState, { payload: { data } }) => {
      return {
        ...prevState,
        count: getOrSelect(data, nextPreviousPaginationAdapter.count),
        current: getOrSelect(data, nextPreviousPaginationAdapter.current),
        next: getOrSelect(data, nextPreviousPaginationAdapter.next),
        previous: getOrSelect(data, nextPreviousPaginationAdapter.previous),
        full_count: data.full_count,
      }
    },
  }),
  {
    name: "Customers",
    effectCaller: rj.configured(),
    effect: (wpAuth) => (params = {}) =>
      api.auth(wpAuth).get(`/api/crm/customer`, params),
    selectors: ({ getPagination }) => ({
      getFullCount: (state) => {
        if (state.root) {
          return get(state, "root.data.pagination.full_count", 0)
        }
        return get(state, "data.pagination.full_count", 0)
      },
    }),
    mutations: {
      create: rj.mutation.single({
        effect: (wpAuth) => (data) => {
          return api.auth(wpAuth).post(`/api/crm/customer`, data)
        },
        updater: (state, result) => {
          return {
            ...state,
            data: {
              ...state.data,
              list: [...state.data.list, result],
            },
          }
        },
      }),
      remove: rj.mutation.single({
        effect: (wpAuth) => (itemId) => {
          return api
            .auth(wpAuth)
            .mapResponse(() => itemId)
            .delete(`/api/crm/customer/${itemId}`)
        },
        updater: (state, result) => {
          return {
            ...state,
            data: {
              ...state.data,
              list: state.data.list.filter((item) => item.id !== result),
            },
          }
        },
      }),
    },
    computed: {
      customers: "getList",
      count: "getCount",
      fullCount: "getFullCount",
      loading: "isPending",
      error: "getError",
      pageCount: "getNumPages",
      hasNext: "hasNext",
      hasPrev: "hasPrev",
    },
  }
)

export function useCustomers(params) {
  return useRunRj(CustomersListRj, [params], false)
}


const CustomersEnumRj = rj(
  {
    name: "CustomersEnum",
    effectCaller: rj.configured(),
    effect: (wpAuth) => (params = {}) =>
      api.auth(wpAuth).get(`/api/crm/customer-enum/`, params),
    computed: {
      customers: "getData",
      loading: "isPending",
      error: "getError",
    },
  }
)

export function useCustomersEnum(params) {
  return useRunRj(CustomersEnumRj, [params], false)
}


const CustomerEnumRj = rj(
  {
    name: "CustomersEnum",
    effectCaller: rj.configured(),
    effect: (wpAuth) => (id) =>
      api.auth(wpAuth).get(`/api/crm/customer-enum/${id}`),
  }
)

export function useCustomerEnum(id) {
  return useRunRj(CustomerEnumRj, [id], true)
}

const CustomerRj = rj({
  name: "Customer",
  effectCaller: rj.configured(),
  effect: (wpAuth) => (id) => api.auth(wpAuth).get(`/api/crm/customer/${id}`),
  mutations: {
    update: rj.mutation.single({
      effect: (wpAuth) => (id, data) => {
        return api.auth(wpAuth).patch(`/api/crm/customer/${id}`, data)
      },
      updater: "updateData",
    }),
  },
  computed: {
    customer: "getData",
    loading: "isPending",
    error: "getError",
  },
})

export function useCustomer(id) {
  return useRunRj(CustomerRj, [id], true)
}

const CustomerStatsRj = rj({
  name: "CustomerStats",
  effectCaller: rj.configured(),
  effect: (wpAuth) => (id) =>
    api.auth(wpAuth).get(`/api/crm/customer/${id}/stats`),
  computed: {
    stats: "getData",
    loading: "isPending",
    error: "getError",
  },
})

export function useCustomerStats(id) {
  return useRunRj(CustomerStatsRj, [id], true)
}
